import { Input } from '@components/common';
import { BookOpen, ChevronRight, GraduationCap, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ACCOUNTING_GUIDE_DOCUMENTS,
  AccountingGuideDocument,
  AccountingGuideLevel,
  AccountingGuideWalkthrough,
} from './accountingGuideContent';

interface HeadingItem {
  id: string;
  depth: 2 | 3;
  title: string;
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const renderInlineMarkdown = (text: string): React.ReactNode[] => {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  let tokenKey = 0;

  return parts.filter(Boolean).map((part) => {
    const key = `${part}-${tokenKey}`;
    tokenKey += 1;

    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={key}
          className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.95em] text-slate-800"
        >
          {part.slice(1, -1)}
        </code>
      );
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={key} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={key}
          href={linkMatch[2]}
          target="_blank"
          rel="noreferrer"
          className="text-pos-primary underline decoration-pos-primary/40 underline-offset-4"
        >
          {linkMatch[1]}
        </a>
      );
    }

    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

const extractHeadings = (markdown: string): HeadingItem[] =>
  markdown
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('## '))
    .map((line) => ({
      id: slugify(line.replace(/^##\s+/, '')),
      depth: 2 as const,
      title: line.replace(/^##\s+/, ''),
    }));

const buildGuideSections = (
  guideDocument: AccountingGuideDocument,
): HeadingItem[] => [
  ...extractHeadings(guideDocument.markdown),
  ...guideDocument.walkthroughs.map((walkthrough) => ({
    id: `walkthrough-${walkthrough.id}`,
    depth: 2 as const,
    title: `${walkthrough.page} example`,
  })),
];

const MarkdownArticle: React.FC<{ markdown: string }> = ({ markdown }) => {
  const lines = markdown.split('\n');
  const blocks: React.ReactNode[] = [];

  let index = 0;
  let codeBlockIndex = 0;

  while (index < lines.length) {
    const rawLine = lines[index];
    const line = rawLine.trim();

    if (!line) {
      index += 1;
    } else if (line.startsWith('```')) {
      const language = line.replace(/^```/, '').trim();
      const codeLines: string[] = [];
      index += 1;

      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }

      blocks.push(
        <pre
          key={`code-${codeBlockIndex}`}
          className="overflow-x-auto rounded-2xl border border-slate-200 bg-slate-950 p-4 text-sm text-slate-100"
        >
          {language ? (
            <div className="mb-2 text-xs uppercase tracking-[0.18em] text-slate-400">
              {language}
            </div>
          ) : null}
          <code>{codeLines.join('\n')}</code>
        </pre>,
      );

      codeBlockIndex += 1;
      index += 1;
    } else if (line.startsWith('# ')) {
      index += 1;
    } else if (line.startsWith('## ')) {
      const title = line.replace(/^##\s+/, '');
      blocks.push(
        <h2
          key={`h2-${slugify(title)}`}
          id={slugify(title)}
          className="scroll-mt-24 border-t border-slate-200 pt-6 text-[1.65rem] font-semibold text-slate-950"
        >
          {title}
        </h2>,
      );
      index += 1;
    } else if (line.startsWith('### ')) {
      const title = line.replace(/^###\s+/, '');
      blocks.push(
        <h3
          key={`h3-${slugify(title)}`}
          id={slugify(title)}
          className="scroll-mt-24 text-base font-semibold text-slate-900"
        >
          {title}
        </h3>,
      );
      index += 1;
    } else if (line.startsWith('- ')) {
      const items: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith('- ')) {
        items.push(lines[index].trim().replace(/^- /, ''));
        index += 1;
      }

      blocks.push(
        <ul
          key={`ul-${index}`}
          className="space-y-2 pl-6 text-[15px] leading-7 text-slate-700"
        >
          {items.map((item) => (
            <li key={item} className="list-disc">
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ul>,
      );
    } else if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s/, ''));
        index += 1;
      }

      blocks.push(
        <ol
          key={`ol-${index}`}
          className="space-y-2 pl-6 text-[15px] leading-7 text-slate-700"
        >
          {items.map((item) => (
            <li key={item} className="list-decimal">
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ol>,
      );
    } else {
      const paragraphLines = [line];
      index += 1;

      while (
        index < lines.length &&
        lines[index].trim() &&
        !/^(#|##|###|- |\d+\.\s|```)/.test(lines[index].trim())
      ) {
        paragraphLines.push(lines[index].trim());
        index += 1;
      }

      const paragraph = paragraphLines.join(' ');
      blocks.push(
        <p key={`p-${index}`} className="text-[15px] leading-7 text-slate-700">
          {renderInlineMarkdown(paragraph)}
        </p>,
      );
    }
  }

  return <article className="space-y-5">{blocks}</article>;
};

const GuideWalkthroughCard: React.FC<{
  walkthrough: AccountingGuideWalkthrough;
}> = ({ walkthrough }) => (
  <section
    id={`walkthrough-${walkthrough.id}`}
    className="scroll-mt-24 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm"
  >
    <div className="flex flex-col gap-2 border-b border-slate-200 pb-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-pos-primary">
        {walkthrough.page}
      </p>
      <h3 className="text-xl font-semibold text-slate-950">
        {walkthrough.title}
      </h3>
      <p className="text-sm leading-6 text-slate-600">{walkthrough.summary}</p>
    </div>

    <div className="mt-4 space-y-4">
      {walkthrough.images.map((image) => (
        <figure
          key={image.src}
          className="overflow-hidden rounded-[20px] border border-slate-200 bg-slate-50"
        >
          <img
            src={image.src}
            alt={image.alt}
            className="w-full object-cover object-top"
          />
          {image.caption ? (
            <figcaption className="border-t border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-600">
              {image.caption}
            </figcaption>
          ) : null}
        </figure>
      ))}

      <div className="rounded-[20px] border border-pos-primary/15 bg-pos-primary/5 px-4 py-4">
        <h4 className="text-sm font-semibold text-slate-950">
          What each field means
        </h4>
        <div className="mt-3 divide-y divide-pos-primary/10">
          {walkthrough.fields.map((field) => (
            <div
              key={`${walkthrough.id}-${field.field}`}
              className="py-3 first:pt-0 last:pb-0"
            >
              <p className="text-sm font-semibold text-slate-950">
                {field.field}
              </p>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                {field.meaning}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                <span className="font-medium text-slate-900">Example:</span>{' '}
                {field.example}
              </p>
            </div>
          ))}
        </div>

        {walkthrough.tips?.length ? (
          <div className="mt-4 border-t border-pos-primary/10 pt-4">
            <h4 className="text-sm font-semibold text-slate-950">
              Before you save
            </h4>
            <ul className="mt-2 space-y-2 pl-5 text-sm leading-6 text-slate-700">
              {walkthrough.tips.map((tip) => (
                <li key={`${walkthrough.id}-${tip}`} className="list-disc">
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  </section>
);

export const AccountingGuidePage: React.FC = () => {
  const [level, setLevel] = useState<AccountingGuideLevel>('beginner');
  const [filterQuery, setFilterQuery] = useState('');

  const guideDocument = useMemo(
    () =>
      ACCOUNTING_GUIDE_DOCUMENTS.find((entry) => entry.level === level) ||
      ACCOUNTING_GUIDE_DOCUMENTS[0],
    [level],
  );

  const headings = useMemo(
    () => buildGuideSections(guideDocument),
    [guideDocument],
  );

  const filteredHeadings = useMemo(() => {
    const query = filterQuery.trim().toLowerCase();
    if (!query) return headings;

    return headings.filter((heading) =>
      heading.title.toLowerCase().includes(query),
    );
  }, [filterQuery, headings]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [level]);

  const handleJumpToSection = (sectionId: string) => {
    const element = window.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[28px] border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/60 to-emerald-100/70 p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-pos-primary shadow-sm">
              <BookOpen size={14} />
              Accounting Guide
            </div>
            <h2 className="mt-4 text-[2rem] font-semibold tracking-tight text-slate-950">
              Learn the accounting feature at your own level
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-700">
              This page works like a built-in documentation hub. Choose your
              experience level, browse the sections on the left, and use the
              guide as the living reference for how accounting works in smartPOS
              Cloud.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {ACCOUNTING_GUIDE_DOCUMENTS.map((entry) => {
              const isActive = entry.level === level;

              return (
                <button
                  key={entry.level}
                  type="button"
                  onClick={() => setLevel(entry.level)}
                  className={[
                    'rounded-2xl border p-4 text-left shadow-sm transition-all',
                    isActive
                      ? 'border-pos-primary bg-pos-primary text-white'
                      : 'border-white/70 bg-white/80 text-slate-700 hover:border-emerald-200 hover:bg-white',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold">{entry.label}</span>
                    <GraduationCap size={16} />
                  </div>
                  <p
                    className={[
                      'mt-2 text-xs leading-5',
                      isActive ? 'text-white/85' : 'text-slate-500',
                    ].join(' ')}
                  >
                    {entry.audience}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="xl:sticky xl:top-6 xl:self-start">
          <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                {guideDocument.label} guide
              </p>
              <h3 className="mt-2 text-base font-semibold text-slate-950">
                {guideDocument.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {guideDocument.summary}
              </p>
            </div>

            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                value={filterQuery}
                onChange={(value) => setFilterQuery(String(value))}
                placeholder="Filter sections"
                className="pl-9"
              />
            </div>

            <div className="mt-4 max-h-[480px] space-y-1 overflow-y-auto pr-1">
              {filteredHeadings.length ? (
                filteredHeadings.map((heading) => (
                  <button
                    key={heading.id}
                    type="button"
                    onClick={() => handleJumpToSection(heading.id)}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950"
                  >
                    <span className="truncate">{heading.title}</span>
                    <ChevronRight size={14} className="flex-shrink-0" />
                  </button>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  No sections match that filter.
                </div>
              )}
            </div>
          </div>
        </aside>

        <section className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Documentation
              </p>
              <h1 className="mt-2 text-[1.9rem] font-semibold tracking-tight text-slate-950">
                {guideDocument.title}
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-500">
                {guideDocument.summary}
              </p>
            </div>
          </div>

          <MarkdownArticle markdown={guideDocument.markdown} />

          <div className="mt-8 space-y-4 border-t border-slate-200 pt-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-pos-primary">
                Guided examples
              </p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                See how to fill the main accounting forms
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Use these examples when you are not sure what each field means.
                They match the actual screens in the accounting module and give
                you a safe starting point before you save records.
              </p>
            </div>

            <div className="space-y-5">
              {guideDocument.walkthroughs.map((walkthrough) => (
                <GuideWalkthroughCard
                  key={walkthrough.id}
                  walkthrough={walkthrough}
                />
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
